import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline, flagOutline, golfOutline, ribbonOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { GameSetup } from './types';

interface AdvancedSettingsProps {
  initialData: Partial<GameSetup>;
  onNext: (data: Partial<GameSetup>) => void;
  onBack: () => void;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({ initialData, onNext, onBack }) => {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [settings, setSettings] = useState({
    contests: {
      longestDrive: initialData.contests?.longestDrive || false,
      closestToPin: initialData.contests?.closestToPin || false,
    },
    teeOptions: {
      default: initialData.teeOptions?.default || 'white',
    },
    useHandicaps: initialData.useHandicaps || true,
  });

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleSubmit = () => {
    onNext({
      ...initialData,
      contests: settings.contests,
      teeOptions: settings.teeOptions,
      useHandicaps: settings.useHandicaps,
    });
  };

  const toggleContest = (contest: 'longestDrive' | 'closestToPin') => {
    setSettings({
      ...settings,
      contests: {
        ...settings.contests,
        [contest]: !settings.contests[contest],
      },
    });
  };

  const setDefaultTee = (tee: 'white' | 'yellow' | 'red' | 'blue') => {
    setSettings({
      ...settings,
      teeOptions: {
        ...settings.teeOptions,
        default: tee,
      },
    });
  };

  return (
    <div className="flex flex-col h-full p-4">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h2 className="text-xl font-semibold">{t('Advanced Settings')}</h2>
        <p className="text-muted-foreground text-sm">
          {t('Configure additional options for your game')}
        </p>
      </motion.div>

      <div className="flex-1 overflow-auto space-y-4">
        {/* Contests Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => toggleSection('contests')}
            className="w-full p-4 flex items-center justify-between bg-white dark:bg-gray-800 text-left"
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 mr-3">
                <IonIcon icon={flagOutline} className="size-5" />
              </div>
              <div>
                <h3 className="font-medium">{t('Contests')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Add special contests to your game')}
                </p>
              </div>
            </div>
            <IonIcon
              icon={expandedSection === 'contests' ? chevronUpOutline : chevronDownOutline}
              className="text-gray-400 size-5"
            />
          </button>
          
          <AnimatePresence>
            {expandedSection === 'contests' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 pt-0 space-y-3 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <div>
                      <h4 className="font-medium">{t('Longest Drive')}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Track the longest drive on selected holes')}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleContest('longestDrive')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.contests.longestDrive
                          ? 'bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          settings.contests.longestDrive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <div>
                      <h4 className="font-medium">{t('Closest to Pin')}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Track closest to pin on par 3 holes')}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleContest('closestToPin')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.contests.closestToPin
                          ? 'bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          settings.contests.closestToPin ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Tee Options Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => toggleSection('tees')}
            className="w-full p-4 flex items-center justify-between bg-white dark:bg-gray-800 text-left"
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
                <IonIcon icon={golfOutline} className="size-5" />
              </div>
              <div>
                <h3 className="font-medium">{t('Tee Options')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Set default tee box for all players')}
                </p>
              </div>
            </div>
            <IonIcon
              icon={expandedSection === 'tees' ? chevronUpOutline : chevronDownOutline}
              className="text-gray-400 size-5"
            />
          </button>
          
          <AnimatePresence>
            {expandedSection === 'tees' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 pt-0 space-y-3 bg-gray-50 dark:bg-gray-800/50">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setDefaultTee('white')}
                      className={`p-3 rounded-lg text-center border transition-all ${
                        settings.teeOptions.default === 'white'
                          ? 'bg-white border-blue-500 dark:border-blue-400 font-medium'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-white border border-gray-300 mx-auto mb-1"></div>
                      {t('White')}
                    </button>
                    
                    <button
                      onClick={() => setDefaultTee('yellow')}
                      className={`p-3 rounded-lg text-center border transition-all ${
                        settings.teeOptions.default === 'yellow'
                          ? 'bg-white border-blue-500 dark:border-blue-400 font-medium'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-yellow-400 mx-auto mb-1"></div>
                      {t('Yellow')}
                    </button>
                    
                    <button
                      onClick={() => setDefaultTee('red')}
                      className={`p-3 rounded-lg text-center border transition-all ${
                        settings.teeOptions.default === 'red'
                          ? 'bg-white border-blue-500 dark:border-blue-400 font-medium'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-red-500 mx-auto mb-1"></div>
                      {t('Red')}
                    </button>
                    
                    <button
                      onClick={() => setDefaultTee('blue')}
                      className={`p-3 rounded-lg text-center border transition-all ${
                        settings.teeOptions.default === 'blue'
                          ? 'bg-white border-blue-500 dark:border-blue-400 font-medium'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-blue-500 mx-auto mb-1"></div>
                      {t('Blue')}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Handicap Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => toggleSection('handicaps')}
            className="w-full p-4 flex items-center justify-between bg-white dark:bg-gray-800 text-left"
          >
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 mr-3">
                <IonIcon icon={ribbonOutline} className="size-5" />
              </div>
              <div>
                <h3 className="font-medium">{t('Handicaps')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('Configure handicap settings')}
                </p>
              </div>
            </div>
            <IonIcon
              icon={expandedSection === 'handicaps' ? chevronUpOutline : chevronDownOutline}
              className="text-gray-400 size-5"
            />
          </button>
          
          <AnimatePresence>
            {expandedSection === 'handicaps' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 pt-0 space-y-3 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <div>
                      <h4 className="font-medium">{t('Use Handicaps')}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('Apply handicaps to scoring')}
                      </p>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, useHandicaps: !settings.useHandicaps })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.useHandicaps
                          ? 'bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          settings.useHandicaps ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      
      <div className="mt-auto pt-4 grid grid-cols-2 gap-3">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={onBack}
          className="py-3 px-4 rounded-xl font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {t('Back')}
        </motion.button>
        
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={handleSubmit}
          className="py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
        >
          {t('Continue')}
        </motion.button>
      </div>
    </div>
  );
};

export default AdvancedSettings;
